import { cn } from '@/lib/utils'

/**
 * Sistema unificado de cores para badges/pills em toda a aplicação.
 * 8 tons fixos cobrem as situações que apareciam espalhadas (em ~9
 * implementações inline + 3 componentes próprios antes da consolidação).
 *
 * Diretrizes de uso:
 * - `success`  → concluído, pago, ativo, aceito
 * - `warning`  → pendente, em aberto, atenção
 * - `danger`   → vencido, bloqueado, vermelho de problema
 * - `info`     → atribuído, parcial — estado neutro de progresso
 * - `purple`   → em trânsito (estado intermediário do frete)
 * - `teal`     → entregue (estado intermediário do frete)
 * - `indigo`   → canhoto recebido (estado intermediário do frete)
 * - `neutral`  → inativo, cancelado, expirado
 */
export type BadgeTone =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'purple'
  | 'teal'
  | 'indigo'
  | 'neutral'

interface BadgeProps {
  readonly tone?: BadgeTone
  readonly icon?: React.ReactNode
  readonly className?: string
  readonly children: React.ReactNode
}

const TONE_CLASSES: Record<BadgeTone, string> = {
  success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-700',
  info: 'bg-blue-100 text-blue-700',
  purple: 'bg-purple-100 text-purple-700',
  teal: 'bg-teal-100 text-teal-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  neutral: 'bg-gray-100 text-gray-500',
}

export function Badge({ tone = 'neutral', icon, className, children }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  )
}
